import { DateTime } from 'luxon'
import { RRule } from 'rrule'

const DEFAULT_TIMES = 100

/**
 * A utility service for parsing and handling iCalendar RRule strings.
 */
export class RRuleService {
	/**
	 * Extracts the value of a specific property from an RRule string.
	 * @param rrule The RRule string (e.g., 'FREQ=WEEKLY;DTSTART=...').
	 * @param property The property to extract (e.g., 'DTSTART', 'UNTIL').
	 * @returns The value of the property, or null if not found.
	 */
	private static getRuleValue(rrule: string, property: string): string | null {
		const rules = rrule.split(';')
		const propertyUpper = property.toUpperCase()

		const rule = rules.find((r) =>
			r.toUpperCase().startsWith(propertyUpper + '=')
		)

		if (rule) {
			return rule.split('=')[1]
		}

		return null
	}

	/**
	 * Parses an RRule date string into a Luxon DateTime object.
	 * @param dateString The date string from the RRule (e.g., '20240520T080000Z').
	 * @returns A Luxon DateTime object, or null if the string is invalid.
	 */
	private static parseDate(dateString: string | null): DateTime | null {
		if (!dateString) {
			return null
		}

		// Support both legacy extended ISO (yyyy-MM-ddTHH:mm:ss) and
		// RFC-style basic UTC (yyyyLLddTHHmmssZ) used in RRULE fields.
		let dt = DateTime.fromISO(dateString)

		if (!dt.isValid) {
			// Try RFC RRULE basic format with Z
			dt = DateTime.fromFormat(dateString, "yyyyLLdd'T'HHmmss'Z'", {
				zone: 'utc',
			})
		}

		if (!dt.isValid) {
			// Try RFC RRULE basic format without Z (fallback)
			dt = DateTime.fromFormat(dateString, "yyyyLLdd'T'HHmmss", {
				zone: 'utc',
			})
		}

		return dt.isValid ? dt : null
	}

	/**
	 * Gets the start date (DTSTART) from an RRule string.
	 * @param rrule The RRule string.
	 * @returns A Luxon DateTime object representing the start date, or null.
	 */
	public static getDtStart(rrule: string | null): DateTime | null {
		if (!rrule) return null
		const dtstartValue = this.getRuleValue(rrule, 'DTSTART')
		return this.parseDate(dtstartValue)
	}

	/**
	 * Gets the end date (UNTIL) from an RRule string.
	 * @param rrule The RRule string.
	 * @returns A Luxon DateTime object representing the end date, or null.
	 */
	public static getUntil(rrule: string | null): DateTime | null {
		if (!rrule) return null
		const untilValue = this.getRuleValue(rrule, 'UNTIL')
		return this.parseDate(untilValue)
	}

	/**
	 * A convenience method to get both the start and end dates from an RRule string.
	 * @param rrule The RRule string.
	 * @returns An object containing `dtstart` and `until` as Luxon DateTime objects or null.
	 */
	public static getRecurrenceDates(rrule: string | null): {
		dtstart: DateTime | null
		until: DateTime | null
	} {
		return {
			dtstart: this.getDtStart(rrule),
			until: this.getUntil(rrule),
		}
	}

	/**
	 * Generates preview data for an RRule string.
	 * @param rruleString The RRule string to generate preview data for.
	 * @param filterFromDate Optional date to filter results from (defaults to current date).
	 * @returns Array of rule data objects with formatted date information.
	 */
	public static generateRuleData(
		rruleString: string,
		filterFromDate?: Date,
		exdates?: string[]
	): Array<{
		day: string
		month: string
		year: string
		time: string
		gmt: string
		date: Date
	}> {
		const { origOptions } = RRule.fromString(rruleString)

		// By default we'll always generate DEFAULT_TIMES results
		let times = DEFAULT_TIMES
		// If the frequency is minutely, we'll probably have more than DEFAULT_TIMES results
		// So, we'll fall into a simple rule to generate the amount of times enough to cover a whole day
		if (origOptions.freq === RRule.MINUTELY) {
			// We gonna divide the number of minutes of a day by the interval chosen, if provided
			// This will be ceiled, so we'll have at least the total + 1 results if not accurate
			times = origOptions.interval
				? Math.ceil(1440 / origOptions.interval)
				: 300

			// Also, if a high number of minutes is provided, we'll have much less results
			// and we'll chose the highest number of results between this and the default number
			times = Math.max(times, DEFAULT_TIMES)
		}

		const rruleTemp = new RRule(RRule.fromString(rruleString).origOptions).all(
			function (_, i) {
				return i < times
			}
		)

		if (rruleTemp) {
			let rruleData = []
			for (var i = 0; i < rruleTemp.length; i++) {
				const dateTime = DateTime.fromJSDate(rruleTemp[i])
				rruleData.push({
					day: dateTime.toFormat('cccc, dd'),
					month: dateTime.toFormat('MMMM'),
					year: dateTime.toFormat('yyyy'),
					time: dateTime.toFormat('HH:mm:ss'),
					gmt: dateTime.toFormat('ZZ'),
					date: rruleTemp[i],
				})
			}

			const filterFrom = filterFromDate || new Date()

			// Filter the rows to show, so we display only future dates
			const newRules = rruleData.filter((item) => {
				return item.date > filterFrom
			})

			// There are special cases when there are rules that start in the past, for these rules what we gonna have is an empty
			// array as the result of the now filter. To avoid not displaying anything, we gonna return the original array if there are no rules
			rruleData = newRules.length > 0 ? newRules : rruleData

			return this.filterExdates(rruleData, exdates)
		}

		return []
	}

	/**
	 * Filters out excluded dates from the rule data.
	 * @param rruleData Array of rule data objects to filter.
	 * @param exdates Array of excluded dates in YYYYMMDD or YYYY-MM-DD format.
	 * @returns Filtered array of rule data objects.
	 */
	public static filterExdates(
		rruleData: Array<{
			day: string
			month: string
			year: string
			time: string
			gmt: string
			date: Date
		}>,
		exdates: string[] = []
	): Array<{
		day: string
		month: string
		year: string
		time: string
		gmt: string
		date: Date
	}> {
		let exdatesData = []
		for (let index = 0; index < exdates.length; index++) {
			const item = exdates[index]
			try {
				let exdate = DateTime.fromFormat(item, 'yyyyMMdd')
				if (exdate.isValid) {
					exdatesData.push({
						day: exdate.toFormat('cccc, dd'),
						month: exdate.toFormat('MMMM'),
						year: exdate.toFormat('yyyy'),
						time: exdate.toFormat('HH:mm:ss'),
						gmt: exdate.toFormat('ZZ'),
					})
				}
			} catch (e) {
				console.log(e)
				let exdate = DateTime.fromFormat(item, 'yyyy-MM-dd')
				if (exdate.isValid) {
					exdatesData.push({
						day: exdate.toFormat('cccc, dd'),
						month: exdate.toFormat('MMMM'),
						year: exdate.toFormat('yyyy'),
						gmt: exdate.toFormat('ZZ'),
					})
				}
			}
		}

		for (let index = 0; index < exdatesData.length; index++) {
			const exdate = exdatesData[index]
			rruleData = rruleData.filter(
				(data) =>
					!(
						exdate.day === data.day &&
						exdate.month === data.month &&
						exdate.year === data.year
					)
			)
		}

		if (rruleData.length > 50) {
			rruleData = rruleData.filter((_, i) => {
				return i < 50
			})
		}

		return rruleData
	}
}
